'use client';

import { cn } from '@bazaar-marketplace/utils';
import Image from 'next/image';
import type { ReactNode } from 'react';
import { forwardRef, useMemo } from 'react';

import { VerifiedText } from '../typography/verified-text';
import { collectionCardVariants, rarityBadgeVariants } from '../collection-card/variants';
import { getRarityName, getRarityVariant } from '../collection-card/utils';
import { RarityBadge } from '../rarity-badge/rarity-badge';
import { MintIndicator } from '~/module/mint/mint-indicator';
import SvgExternalIndicator from '@bazaar-marketplace/assets/external-indicator';
import type { CollectionResponseDtoRarity } from '~/lib/api';

interface BaseCardProps {
  label?: number | string;
  rarity?: CollectionResponseDtoRarity;
  imageSrc: string | null;
  imagePosition?: 'contain' | 'cover';
  title: string;
  brandName?: string | null;
  className?: string;
  badgeText?: string;
  isMinted?: boolean;
  isExternal?: boolean;
  bottomContent?: ReactNode;
  titleHeight?: number;
  cardHeight?: number;
  overlayContent?: ReactNode;
}

export const BaseCard = forwardRef<HTMLDivElement, BaseCardProps & React.HTMLAttributes<HTMLDivElement>>(
  (
    {
      label,
      rarity,
      imageSrc,
      imagePosition = 'cover',
      title,
      brandName,
      className,
      badgeText,
      isMinted,
      isExternal,
      bottomContent,
      titleHeight,
      cardHeight,
      overlayContent,
      ...props
    },
    ref,
  ) => {
    const rarityVariant = rarity ? getRarityVariant(rarity) : 'common';

    const fontSize = useMemo(() => {
      if (title.length > 20) {
        return 'text-[12px]';
      }
      return 'text-lg';
    }, [title]);

    function renderRightContent() {
      if (isExternal) {
        return <SvgExternalIndicator />;
      }
      if (isMinted) {
        return <MintIndicator />;
      }
      if (badgeText) {
        // Use RarityBadge if badgeText matches rarity name (not a percentage)
        const isRarityName = rarity && badgeText === getRarityName(rarity);
        if (isRarityName) {
          return <RarityBadge className="text-[11px]" rarity={rarity} textClassName="text-[11px]" />;
        }
        // Otherwise use the old way for percentages
        return (
          <div className={rarityBadgeVariants({ rarity: rarityVariant })}>
            <span className="text-white text-[11px] font-bold">{badgeText}</span>
          </div>
        );
      }
      return null;
    }

    const renderLabelText = () => {
      if (!label) {
        return null;
      }
      if (typeof label === 'string') {
        return label;
      }
      return `${label}x`;
    };

    return (
      <div
        ref={ref}
        className={cn(collectionCardVariants({ rarity: rarityVariant }), 'relative', className)}
        style={{ height: cardHeight ?? 272 }}
        {...props}
      >
        <div className="flex justify-between items-center w-full gap-4">
          <div className="flex items-center gap-1">
            {!isExternal && badgeText ? null : (
              <div className={cn(rarityBadgeVariants({ rarity: rarityVariant }), 'size-3 shrink-0 p-0')} />
            )}

            {renderLabelText() ? (
              <span className="text-white/60 text-[12px] font-bold">{renderLabelText()}</span>
            ) : null}
          </div>

          <div>{renderRightContent()}</div>
        </div>

        <div className="w-full relative h-28 rounded-2xl flex items-center justify-center overflow-hidden">
          {imageSrc ? (
            <Image
              alt={title}
              className={cn('w-full h-full', imagePosition === 'cover' ? 'object-cover' : 'object-contain')}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              src={imageSrc}
            />
          ) : (
            <div className="bg-gray-100" style={{ height: 112, width: 141 }} />
          )}
        </div>

        <div className={cn('flex flex-col justify-end items-center w-full grow')}>
          <div
            className="flex flex-col items-end justify-end gap-1 overflow-hidden"
            style={{ height: titleHeight ?? 50 }}
          >
            <h3 className={cn('text-white font-bold font-quicksand text-center', fontSize)}>{title}</h3>
          </div>

          {brandName ? (
            <div className="grid grid-cols-[auto] items-center justify-between gap-1">
              <span className="truncate whitespace-nowrap text-[12px] text-white/60 font-bold gap-1">
                <VerifiedText singleLine svgClassName="size-[14px]" text={brandName} />
              </span>
            </div>
          ) : null}
        </div>

        {bottomContent}

        {overlayContent ? (
          <div
            className="absolute inset-0 z-30 flex items-center justify-center rounded-[28px] overflow-hidden"
            style={{ background: 'rgba(0,0,0,.6)' }}
          >
            <div className="relative z-10">{overlayContent}</div>
          </div>
        ) : null}
      </div>
    );
  },
);

BaseCard.displayName = 'BaseCard';
